import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

import { mapShopperResponse } from '@core/models/shopper.mapper';
import { ShopperResponse } from '@core/models/shopper.response';
import { ShopperState } from '@core/models/shopper.state';
import { ApiService } from '@core/services/api.service';

import {
  OAuthProvider,
  SaveShopperAddressRequest,
  SaveShopperPaymentMethodRequest,
  ShopperRegisterRequest,
  ShopperRepository,
  UpdateShopperNotificationsRequest,
  UpdateShopperProfileRequest
} from './shopper.repository';

@Injectable()
export class ShopperHttpRepository implements ShopperRepository {
  private readonly api = inject(ApiService);

  signIn(email: string, password: string): Observable<ShopperState> {
    return this.api.post<ShopperResponse>('shoppers/sign-in', { email, password }).pipe(map(mapShopperResponse));
  }

  register(request: ShopperRegisterRequest): Observable<ShopperState> {
    return this.api.post<ShopperResponse>('shoppers/register', request).pipe(map(mapShopperResponse));
  }

  getById(id: number): Observable<ShopperState | null> {
    return this.api
      .get<ShopperResponse | null>(`shoppers/${id}`)
      .pipe(map(response => (response ? mapShopperResponse(response) : null)));
  }

  updateProfile(shopperId: number, request: UpdateShopperProfileRequest): Observable<ShopperState> {
    return this.api.put<ShopperResponse>(`shoppers/${shopperId}/profile`, request).pipe(map(mapShopperResponse));
  }

  changePassword(shopperId: number, currentPassword: string, newPassword: string): Observable<void> {
    return this.api.post<void>(`shoppers/${shopperId}/password`, { currentPassword, newPassword });
  }

  saveAddress(shopperId: number, request: SaveShopperAddressRequest): Observable<ShopperState> {
    const call =
      request.id === null
        ? this.api.post<ShopperResponse>(`shoppers/${shopperId}/addresses`, request)
        : this.api.put<ShopperResponse>(`shoppers/${shopperId}/addresses/${request.id}`, request);
    return call.pipe(map(mapShopperResponse));
  }

  deleteAddress(shopperId: number, addressId: number): Observable<ShopperState> {
    return this.api
      .delete<ShopperResponse>(`shoppers/${shopperId}/addresses/${addressId}`)
      .pipe(map(mapShopperResponse));
  }

  setDefaultAddress(shopperId: number, addressId: number): Observable<ShopperState> {
    return this.api
      .put<ShopperResponse>(`shoppers/${shopperId}/addresses/${addressId}/default`, {})
      .pipe(map(mapShopperResponse));
  }

  updateNotifications(shopperId: number, request: UpdateShopperNotificationsRequest): Observable<ShopperState> {
    return this.api.put<ShopperResponse>(`shoppers/${shopperId}/notifications`, request).pipe(map(mapShopperResponse));
  }

  savePaymentMethod(shopperId: number, request: SaveShopperPaymentMethodRequest): Observable<ShopperState> {
    return this.api
      .post<ShopperResponse>(`shoppers/${shopperId}/payment-methods`, request)
      .pipe(map(mapShopperResponse));
  }

  deletePaymentMethod(shopperId: number, paymentMethodId: number): Observable<ShopperState> {
    return this.api
      .delete<ShopperResponse>(`shoppers/${shopperId}/payment-methods/${paymentMethodId}`)
      .pipe(map(mapShopperResponse));
  }

  setDefaultPaymentMethod(shopperId: number, paymentMethodId: number): Observable<ShopperState> {
    return this.api
      .put<ShopperResponse>(`shoppers/${shopperId}/payment-methods/${paymentMethodId}/default`, {})
      .pipe(map(mapShopperResponse));
  }

  signInWithProvider(provider: OAuthProvider, email: string): Observable<ShopperState> {
    return this.api.post<ShopperResponse>('shoppers/sign-in/oauth', { provider, email }).pipe(map(mapShopperResponse));
  }
}
